import React, { FC, Suspense, useContext, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RouteComponentProps, useHistory, useLocation } from "react-router";
import { wrapPromise } from "go-core";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import EmptyList from "go-core/components/EmptyList";
import { LoadingContainer } from "go-core/components/Loading";
import { StickyColumnWithEntityStatus } from "go-core/components/StickyColumnWithEntityStatus";
import { useWindowSize } from "go-core/components/useWindowSize";
import { FilterCondition, ListFilterSource } from "go-list/core/components/Filter/services/types";
import { ListData } from "go-list/list";
import { getSelectedSegmentForListConfig } from "go-list/list/services/segment-service";
import { GoListSegmentType, ListConfig, ListParamsType, MultipleActionsParams } from "go-list/list/services/types";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import { apiOrganization } from "../../../../../../../../../../../../services/Api/Organization/apiOrganization";
import { MenuPriceListApi } from "../../../../../../../../../../../../services/Api/Organization/types";
import { convertListStatusToEntityStatus } from "../../../../../../../../../../../../utils/entityStatus/entityStatus";
import SubmenuPriceListsNavigation from "../../../../components/SubmenuPriceListsNavigation";
import { getInitialSpreadsheetPriceListDefaultColumns } from "../../../GroupEditing/pages/Spreadsheet/utils";

interface ListState {
	resource?: Record<string, any>;
	mobileActions: MobileActionProps[];
}

const listName = "MENU_PRICE_LIST";
const resourceType = "MENU_PRICE_LIST";

const List: FC<ListState> = ({ resource, mobileActions }) => {
	const { t } = useTranslation();
	const history = useHistory();
	const location = useLocation();
	const organization = useSelector(selectOrganization);
	const { addSuccessFlash } = useFlash();
	const segmentContext = useContext(SegmentContext);

	if (!resource) return null;
	const data = resource.read();

	const onRemovePriceList = async (item: MenuPriceListApi) => {
		await apiOrganization.removePriceList(item.id);
		addSuccessFlash(t("common.flash.removed", { ns: "lib" }));
	};

	const onRestorePriceList = async (item: MenuPriceListApi) => {
		await apiOrganization.restorePriceList(item.id);
		addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
	};

	const onDisablePriceList = async (item: MenuPriceListApi) => {
		await apiOrganization.disablePriceList(item.id);
		addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
	};

	const removePriceLists = async (
		multipleActionsItems: MenuPriceListApi[],
		_?: ListParamsType,
		areAllItemsSelected?: boolean,
		multipleActionParams?: MultipleActionsParams
	) => {
		const idsAsString = multipleActionsItems.map((item) => item.id.toString()).join(",");
		await apiOrganization.removePriceLists(idsAsString, multipleActionParams, areAllItemsSelected);
		addSuccessFlash(t("common.flash.removed", { ns: "lib" }));
	};

	const restorePriceLists = async (
		multipleActionsItems: MenuPriceListApi[],
		_?: ListParamsType,
		areAllItemsSelected?: boolean,
		multipleActionParams?: MultipleActionsParams
	) => {
		const idsAsString = multipleActionsItems.map((item) => item.id.toString()).join(",");
		await apiOrganization.restorePriceLists(idsAsString, multipleActionParams, areAllItemsSelected);
		addSuccessFlash(t("lib:common.flash.completed"));
	};

	const disablePriceLists = async (
		multipleActionsItems: MenuPriceListApi[],
		_?: ListParamsType,
		areAllItemsSelected?: boolean,
		multipleActionParams?: MultipleActionsParams
	) => {
		const idsAsString = multipleActionsItems.map((item) => item.id.toString()).join(",");
		await apiOrganization.disablePriceLists(idsAsString, multipleActionParams, areAllItemsSelected);
		addSuccessFlash(t("lib:common.flash.completed"));
	};

	const onEditPrices = (item: MenuPriceListApi) => {
		const defaultColumns = getInitialSpreadsheetPriceListDefaultColumns(t).map((col) => col.id);
		const columns = [...defaultColumns, item.id];
		history.push({
			pathname: `/${organization.id}/menu/price_lists/group_editing`,
			search: `c=${columns.join(",")}`,
		});
	};

	let config = {
		fields: [
			{
				id: "name",
				name: t("lib:common.word.name"),
				render: (item: MenuPriceListApi) => (
					<StickyColumnWithEntityStatus
						minWidth={200}
						to={`${location.pathname}/${item.id}`}
						status={convertListStatusToEntityStatus(item.status)}
					>
						{item.name}
					</StickyColumnWithEntityStatus>
				),
				renderExport: (item: MenuPriceListApi) => item.name,
			},
			{
				id: "availability",
				name: t("common.word.availability"),
				render: (item: MenuPriceListApi) => item?.availability?.name,
			},
		],
		filters: [
			{
				id: "name",
				name: t("lib:common.word.name"),
				type: "text",
				typeOptions: ["c", "e"],
			},
			{
				id: "availability",
				name: t("common.word.availability"),
				type: "search_select",
				source: {
					request: (search: string, filterParams: Record<string, any>, options?: Record<string, any>) =>
						apiOrganization.getAvailabilitiesSearchSelect(search, filterParams, {
							cancelToken: options?.token,
						}),
				} as ListFilterSource,
			},
			{
				id: "status",
				name: t("common.word.status", { ns: "lib" }),
				type: "list",
				options: {
					ENABLED: t("enums.common.status.ENABLED", { ns: "lib" }),
					DISABLED: t("enums.common.status.DISABLED", { ns: "lib" }),
					DELETED: t("enums.common.status.DELETED", { ns: "lib" }),
				},
				hasDefaultOptions: false,
			},
		],
		selectedColumns: ["name", "availability"],
		actions: [
			{
				name: t("common.action.edit", { ns: "lib" }),
				link: (item: MenuPriceListApi) => `${location.pathname}/${item.id}`,
			},
			{
				name: t("modules.price_list.action.edit_prices.title"),
				click: (item: MenuPriceListApi) => onEditPrices(item),
			},
			{
				name: t("common.action.remove", { ns: "lib" }),
				click: (item: MenuPriceListApi) => onRemovePriceList(item),
				visible: (item: MenuPriceListApi) => item.status !== "DELETED",
				refreshListAfter: true,
				confirmation: {
					use: true,
					message: t("confirmation.message.remove", { ns: "lib" }),
				},
			},
			{
				name: t("common.action.disable", { ns: "lib" }),
				click: (item: MenuPriceListApi) => onDisablePriceList(item),
				visible: (item: MenuPriceListApi) => item.status === "ENABLED",
				refreshListAfter: true,
				confirmation: {
					use: true,
					message: t("confirmation.message.disable", { ns: "lib" }),
				},
			},
			{
				name: t("common.action.activate", { ns: "lib" }),
				click: (item: MenuPriceListApi) => onRestorePriceList(item),
				visible: (item: MenuPriceListApi) => item.status === "DISABLED" || item.status === "DELETED",
				refreshListAfter: true,
				confirmation: {
					use: true,
					message: t("confirmation.message.activate", { ns: "lib" }),
				},
			},
		],
		multipleActions: [
			{
				name: t("common.action.remove", { ns: "lib" }),
				click: async (
					multipleActionsItems: MenuPriceListApi[],
					listParams?: ListParamsType,
					areAllItemsSelected?: boolean,
					multipleActionParams?: MultipleActionsParams
				) => removePriceLists(multipleActionsItems, listParams, areAllItemsSelected, multipleActionParams),
				visible: (multipleActionsitems: MenuPriceListApi[]) => {
					return multipleActionsitems.filter((f) => f.status !== "DELETED").length > 0;
				},
				refreshListAfter: true,
				confirmation: {
					use: true,
					message: t("modules.price_list.field.remove_message.title"),
				},
			},
			{
				name: t("common.action.activate", { ns: "lib" }),
				click: async (
					multipleActionsItems: MenuPriceListApi[],
					listParams?: ListParamsType,
					areAllItemsSelected?: boolean,
					multipleActionParams?: MultipleActionsParams
				) => restorePriceLists(multipleActionsItems, listParams, areAllItemsSelected, multipleActionParams),
				visible: (multipleActionsitems: MenuPriceListApi[]) => {
					return (
						multipleActionsitems.filter((f) => f.status === "DELETED" || f.status === "DISABLED").length > 0
					);
				},
				confirmation: {
					use: true,
					message: t("modules.price_list.field.activate_message.title"),
				},
				refreshListAfter: true,
			},
			{
				name: t("common.action.disable", { ns: "lib" }),
				click: async (
					multipleActionsItems: MenuPriceListApi[],
					listParams?: ListParamsType,
					areAllItemsSelected?: boolean,
					multipleActionParams?: MultipleActionsParams
				) => disablePriceLists(multipleActionsItems, listParams, areAllItemsSelected, multipleActionParams),
				visible: (multipleActionsitems: MenuPriceListApi[]) => {
					return multipleActionsitems.filter((f) => f.status === "ENABLED").length > 0;
				},
				refreshListAfter: true,
				confirmation: {
					use: true,
					message: t("modules.price_list.field.disable_message.title"),
				},
			},
		],
		segments: [
			{
				id: "all",
				name: t("common.word.all", { ns: "lib" }),
				slug: "all",
			},
			{
				id: "deleted",
				name: t("go_list.filters.deleted", { ns: "lib" }),
				slug: "deleted",
				filters: [
					{
						filterId: "status",
						value: "DELETED",
						condition: "e" as FilterCondition,
					},
				],
			} as GoListSegmentType,
		],
		selectedSegment: getSelectedSegmentForListConfig(data.segments, "all"),
		exportConfig: {
			title: t("modules.price_list.field.export_config.title"),
			filename: t("modules.price_list.header.title"),
			organization: `${organization.name}`,
			taxIdNo: organization?.more?.print_company_on_pdf ? organization.more?.company_tax_id_no : undefined,
			company: organization?.more?.print_company_on_pdf ? organization.more?.company_name : undefined,
			pdfOrientation: organization?.more?.pdf_orientation,
			pdfFontSize: organization?.more?.default_pdf_font_size?.toString(),
		},
		fetch: (fetchParams: Record<string, any> = {}, sourceToken?: CancelTokenSource) => {
			fetchParams.include = "availability";
			return apiOrganization.getPriceLists(fetchParams, { cancelToken: sourceToken?.token });
		},
		saveSegment: (segment: SegmentType) => {
			return segmentContext.save(listName, resourceType, segment);
		},
		fetchTotalItemsCountForSelectedFilters: (
			fetchTotalItemsParams: Record<string, any>,
			sourceToken?: CancelTokenSource
		) => apiOrganization.getPriceListCount(fetchTotalItemsParams, { cancelToken: sourceToken?.token }),

		doesIdColumnRedirectToPreviewPage: true,
		numberOfStickyColumnsAtTheStart: 1,
	} as ListConfig;
	config = {
		...config,
		externalSegments: data.externalSegments,
		fields: config.fields ? [...config.fields, ...data.fields] : data.fields,
		customFields: data.fields,
		filterValues: data.filter_values,
	};

	return (
		<ListData
			config={config}
			emptyList={
				<EmptyList
					title={t("modules.price_list.header.title")}
					description={t("modules.price_list.field.empty_list_description.title")}
					actions={[
						{
							name: `+ ${t("common.action.add", { ns: "lib" })}`,
							click: () => {
								history.push(`${location.pathname}/new`);
							},
						},
					]}
				/>
			}
			mobileActions={mobileActions}
			canManageMultiActionsForAllItems
		/>
	);
};

const OrganizationMenuPriceListsIndexIndexPage: FC<RouteComponentProps> = (props) => {
	const { t } = useTranslation();
	const isMobile = useWindowSize().isMobile;
	const [resource, setResource] = useState<Record<string, any>>();
	const segmentContext = useContext(SegmentContext);
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		handleChangeTabTitle(t("modules.price_list.header.title"));
		setResource(wrapPromise(segmentContext.get(listName, resourceType)));
	}, []);

	const buttons: ButtonProps[] = [
		{
			title: t("common.action.add", { ns: "lib" }),
			path: `${props.match.url}/new`,
			variant: "primary",
		},
	];
	const mobileActions: MobileActionProps[] = [
		{
			title: t("common.action.add", { ns: "lib" }),
			path: `${props.match.url}/new`,
		},
	];

	return (
		<>
			{!isMobile && <Header title={t("modules.price_list.header.title")} buttons={buttons} />}
			<SubmenuPriceListsNavigation />
			<Suspense fallback={<LoadingContainer />}>
				<List resource={resource} mobileActions={mobileActions} />
			</Suspense>
		</>
	);
};

export default OrganizationMenuPriceListsIndexIndexPage;
